import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  Printer,
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  EyeOff,
  BookOpen,
  Lightbulb,
  Settings2,
  PenLine,
  Check,
  X,
  SkipForward,
  Trophy,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Coins,
  Target,
  Zap,
  Award,
  Rocket,
  Flame,
  Star,
  Clock,
  Timer,
  GraduationCap,
} from "lucide-react";

import { GET_SESSION, SESSION_PLAN_PREVIEW } from "@/graphql/queries/sessions";
import { GET_STUDENT, GET_STUDENT_AVATAR_CREDITS } from "@/graphql/queries/students";
import {
  GENERATE_EXERCISES,
  DELETE_SESSION,
  CHECK_STUDENT_ANSWER,
  COMPLETE_SESSION,
} from "@/graphql/mutations/sessions";
import { useSessionTimer } from "@/hooks/use-session-timer";
import { useDiscussions } from "@/hooks/use-discussions";
import { useSlideParam } from "@/hooks/use-slide-param";
import { useDebounced } from "@/hooks/use-debounced";
import { useTheoryView } from "@/hooks/use-theory-view";
import { DiscussionPanel } from "@/components/discussion/discussion-panel";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SubjectIcon } from "@/components/subject-icon";
import { MathContent } from "@/components/math/math-content";
import { TheoryView } from "@/components/theory-view";
import { ChoiceProblem, isChoiceProblem } from "@/components/math/choice-problem";
import { SessionPlanPreview, SessionPlanSummary } from "@/components/session-plan-preview";
import {
  SessionLengthForecast,
  type PlanLimit,
  type PlannerOverrides,
} from "@/components/session-length-forecast";
import { SessionPersonalization } from "@/components/session-personalization";
import { SkillCategoryBadge } from "@/components/skill-category-badge";
import { NewSkillModal } from "@/components/new-skill-modal";
import { useCelebration } from "@/components/celebrations";
import {
  toCelebrationEvents,
  type GamificationEvent,
  type GamificationResult,
} from "@/lib/gamification";
import {
  checkAnswerAdvanced,
  detectMultiAnswers,
  checkMultiAnswerAdvanced,
  splitFillBlanks,
  checkFillBlanksAdvanced,
  type CheckResult,
} from "@/lib/answer-checking";
import {
  buildAnswerFormats,
  buildAnswerFormatsFor,
  composeAnswer,
  toInputValue,
  type AnswerFormat,
} from "@/lib/answer-format";
import { AnswerAffix, AnswerInput } from "@/components/math/answer-input";
import { SLIDE_ADVANCE_MS } from "@/lib/advance";
import { parseExerciseCount, EXERCISE_COUNT_FALLBACK } from "@/lib/students";
import { ExerciseCountField } from "@/components/exercise-count-field";
import { personalizationLabel } from "@/lib/personalization";
import { useAuth } from "@/lib/auth/AuthContext";
import type {
  Exercise,
  SessionPlanEntry,
  SkillChange,
  MissingSkill,
  Discussion,
} from "@/graphql/types";

type GradeResult = "CORRECT" | "INCORRECT" | "PARTIAL" | "SKIPPED";

function getStatusBadge(status: string) {
  const variants: Record<
    string,
    { label: string; variant: "default" | "secondary" | "outline" }
  > = {
    DRAFT: { label: "Juodraštis", variant: "secondary" },
    GENERATED: { label: "Sugeneruota", variant: "outline" },
    GRADED: { label: "Įvertinta", variant: "default" },
    COMPLETED: { label: "Užbaigta", variant: "default" },
  };
  const { label, variant } = variants[status] || {
    label: status,
    variant: "secondary" as const,
  };
  return <Badge variant={variant}>{label}</Badge>;
}

function SkillChangeIndicator({ change }: { change: number }) {
  if (change > 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
        <TrendingUp className="h-3 w-3" />
        +{change.toFixed(1)}%
      </span>
    );
  } else if (change < 0) {
    return (
      <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
        <TrendingDown className="h-3 w-3" />
        {change.toFixed(1)}%
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
      <Minus className="h-3 w-3" />
      0%
    </span>
  );
}

export default function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { devTools } = useAuth();
  const navigate = useNavigate();
  const { celebrateMultiple, fireConfetti } = useCelebration();
  const { data, loading, refetch } = useQuery(GET_SESSION, {
    variables: { id },
    skip: !id,
  });

  const [generateExercises, { loading: isGenerating }] = useMutation(GENERATE_EXERCISES);
  const [deleteSession] = useMutation(DELETE_SESSION);
  const [checkStudentAnswer] = useMutation(CHECK_STUDENT_ANSWER);
  const [completeSession] = useMutation(COMPLETE_SESSION);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showNewSkillModal, setShowNewSkillModal] = useState(false);
  // How many exercises to generate, as typed. `null` means "untouched, so
  // whatever this learner's profile says"; anything typed here pins it for this
  // session only. Any number in range goes — the presets are a shortcut, not
  // the choice.
  const [countDraft, setCountDraft] = useState<string | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  // Planner rules lifted for the next generation and nothing beyond it. An
  // override is a teacher disagreeing with a cap once, not a setting — so it
  // lives in component state and is gone on the next visit.
  const [overrides, setOverrides] = useState<PlannerOverrides>({});

  // Visibility
  // Which question's theory is open in the side panel, or null. Theory used to
  // be a full-width accordion above the questions that opened *by itself* on a
  // new topic — so the first screen of this page was a wall of explanation and
  // the question was below the fold, eleven times over on a finished session.
  // It is asked for now, one question at a time, and it opens beside the
  // question rather than on top of it.
  const [theoryFor, setTheoryFor] = useState<string | null>(null);
  const [revealedHints, setRevealedHints] = useState<Set<string>>(new Set());

  // Solving/grading state
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [grades, setGrades] = useState<Record<string, GradeResult>>({});
  const [revealedAnswers, setRevealedAnswers] = useState<Set<string>>(new Set());
  const [manuallyGraded, setManuallyGraded] = useState<Set<string>>(new Set());
  const [subAnswerResults, setSubAnswerResults] = useState<Record<string, CheckResult[]>>({});
  const [restoredFromServer, setRestoredFromServer] = useState(false);
  // Session details collapse once exercises exist, so the task itself is the
  // focus. `null` means "follow the data"; a click pins it either way.
  const [detailsOpen, setDetailsOpen] = useState<boolean | null>(null);
  // One exercise on screen at a time while solving. The index lives in the URL
  // (?task=N) so it survives a refresh.
  const [currentSlide, setCurrentSlide] = useSlideParam(
    data?.session?.exercises?.length ?? 0
  );

  // Completion
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompletionSummary, setShowCompletionSummary] = useState(false);
  const [gamificationResult, setGamificationResult] = useState<GamificationResult | null>(null);

  const inputRefs = useRef<Map<string, HTMLElement>>(new Map());
  const session = data?.session;
  // The learner's own session length is the starting point; any other number can
  // be typed over it in the generation dialog.
  const suggestedExerciseCount = session?.student?.suggestedExerciseCount ?? EXERCISE_COUNT_FALLBACK;
  // Clamped as it is read, so what the field shows is what will be generated —
  // the server clamps too, and silently, which reads as the field being ignored.
  const requestedCount = parseExerciseCount(countDraft ?? String(suggestedExerciseCount));

  // What the chosen length will actually come out as. Only worth asking while a
  // generation is still ahead — a graded session's plan is history, not a
  // forecast. Debounced because the length is typed now: a forecast per
  // keystroke would ask the planner about "1", "1_", "15" on the way to 15.
  const canGenerate = session?.status === "DRAFT" || session?.status === "GENERATED";
  const forecastCount = useDebounced(requestedCount, 350);
  const { data: forecastData } = useQuery(SESSION_PLAN_PREVIEW, {
    variables: {
      sessionId: id,
      // Null means "let the learner's own length decide", which the server
      // resolves for itself — sending NaN would ask it for a session of nothing.
      targetExerciseCount: forecastCount,
      maxNewSkills: overrides.maxNewSkills ?? null,
      includeDistantReview: overrides.includeDistantReview ?? null,
    },
    skip: !id || !canGenerate || !session?.subjectKey,
    fetchPolicy: "cache-and-network",
  });
  const forecast = forecastData?.sessionPlanPreview;
  const personalizationBadge = personalizationLabel(
    session?.personalizationStatus,
    session?.personalizedProblemCount
  );

  // How each expected answer is entered (unit label, compound fields, clock),
  // keyed like `answers` entries. Units are appended when the answer is
  // checked, so the student only types numbers.
  const answerFormats = useMemo<Record<string, AnswerFormat>>(() => {
    if (!session) return {};
    return buildAnswerFormatsFor(session.exercises);
  }, [session]);

  // Track active working time while the student is solving. The first
  // unanswered problem is the highlighted/active task, so time is attributed
  // to it (see below for the identical `firstUnansweredId` used for styling).
  const solving = session?.status === "GENERATED";
  const timedProblemId = solving
    ? session!.exercises
        .filter((ex: Exercise) => ex.expectedAnswer)
        .map((ex: Exercise) => ex.id)
        .find((exerciseId: string) => !grades[exerciseId])
    : undefined;
  useSessionTimer(id, solving, timedProblemId);

  // AI adjudication + tutoring chat (opens instantly, adjudicates in background).
  const theoryView = useTheoryView(id);

  const advanceRef = useRef<(problemId: string) => void>(() => {});
  const tutor = useDiscussions({
    sessionId: id,
    onResultChange: (problemId, result, viaChat) => {
      setGrades((prev) => ({ ...prev, [problemId]: result }));
      // The AI accepted the answer outright — there is no conversation to read,
      // so this is simply a correct answer and carries the student on.
      if (result === "CORRECT" && !viaChat) advanceRef.current(problemId);
    },
    onDegraded: (problemId) => setRevealedHints((prev) => new Set(prev).add(problemId)),
    onClosed: (problemId) => setRevealedHints((prev) => new Set(prev).add(problemId)),
  });

  // Restore from server
  useEffect(() => {
    if (!session || restoredFromServer) return;

    const restoredAnswers: Record<string, string> = {};
    const restoredGrades: Record<string, GradeResult> = {};
    const restoredRevealed = new Set<string>();
    const restoredManual = new Set<string>();
    const restoredSubResults: Record<string, CheckResult[]> = {};

    for (const problem of session.exercises) {
      if (problem.answerRevealed) {
        restoredRevealed.add(problem.id);
      }
      if (problem.manuallyGraded) {
        restoredManual.add(problem.id);
      }
      if (problem.result && problem.result !== "PENDING") {
        restoredGrades[problem.id] = problem.result as GradeResult;
      }
      if (problem.userAnswer != null && problem.userAnswer !== "") {
        // Stored answers carry their unit; the input keeps it beside the field.
        const restore = (key: string, value: string) => {
          restoredAnswers[key] = toInputValue(value, answerFormats[key]);
        };

        if (problem.answerType === "fill_blanks") {
          const userParts = problem.userAnswer.split("|");
          for (let i = 0; i < userParts.length; i++) {
            restore(`${problem.id}__${i}`, userParts[i] || "");
          }
        } else {
          const multi = problem.expectedAnswer
            ? detectMultiAnswers(problem.expectedAnswer, problem.problemText, problem.choices)
            : null;
          if (multi) {
            const userParts = problem.userAnswer.split(", ");
            for (let i = 0; i < multi.length; i++) {
              restore(`${problem.id}__${i}`, userParts[i] || "");
            }
            if (problem.result && problem.result !== "PENDING") {
              const { results } = checkMultiAnswerAdvanced(
                multi.map((_, i) => userParts[i] || ""),
                multi,
                problem.answerRules
              );
              restoredSubResults[problem.id] = results;
            }
          } else {
            restore(problem.id, problem.userAnswer);
          }
        }
      }
    }

    setAnswers(restoredAnswers);
    setGrades(restoredGrades);
    setRevealedAnswers(restoredRevealed);
    setManuallyGraded(restoredManual);
    setSubAnswerResults(restoredSubResults);
    tutor.restore(session.exercises);
    setRestoredFromServer(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, restoredFromServer, answerFormats]);

  // Announce a new topic once per session. The modal already existed; nothing
  // ever opened it. localStorage keeps a refresh from replaying the moment.
  useEffect(() => {
    if (!session?.newSkillKey || session.status !== "GENERATED") return;
    if (session.exercises.length === 0) return;

    const seenKey = `new-skill-seen-${session.id}`;
    try {
      if (localStorage.getItem(seenKey)) return;
      localStorage.setItem(seenKey, "1");
    } catch {
      /* private mode — show it, just don't remember */
    }
    setShowNewSkillModal(true);
  }, [session?.id, session?.newSkillKey, session?.status, session?.exercises.length]);

  // `NewSkillModal` no longer offers to open the theory — see its own comment.
  // Theory opened for somebody is theory they scroll past, so the panel asks for
  // itself; the dialog announces the topic and gets out of the way.

  // Auto-focus the first unanswered answer field (or first choice) whenever the
  // exercise on screen changes, so the student can start typing right away.
  // Keyed on the exercise id rather than the session object: a chat reply
  // updates the cached session, and that must not pull focus out of the chat.
  const focusedExerciseId = session?.exercises?.[currentSlide]?.id;
  useEffect(() => {
    if (!session) return;
    if (session.status !== "GENERATED" && session.status !== "GRADED") return;
    const exercise = session.exercises[currentSlide];
    if (!exercise) return;
    // The tutor owns the keyboard while a conversation is open.
    if (tutor.discussions[exercise.id]?.status === "OPEN") return;
    if (!exercise.expectedAnswer || grades[exercise.id]) return;
    const timer = setTimeout(() => {
      inputRefs.current.get(exercise.id)?.focus({ preventScroll: true });
    }, 120);
    return () => clearTimeout(timer);
    // Deliberately not keyed on `grades` — only a new exercise moves focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedExerciseId, restoredFromServer]);

  // Whether there is anywhere to go once a question is behind them. One
  // exercise is one question, so "next" is always the next slide.
  const hasNextSlide = useCallback((): boolean => {
    if (!session) return false;
    const solvingNow = session.status === "GENERATED" || session.status === "GRADED";
    if (!solvingNow || session.exercises.length < 2) return false;
    return Math.min(currentSlide, session.exercises.length - 1) < session.exercises.length - 1;
  }, [session, currentSlide]);

  const goToNextSlide = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrentSlide((s) => s + 1);
  }, [setCurrentSlide]);

  // A correct answer carries the student onward by itself.
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    []
  );

  const advanceAfterCorrect = useCallback(
    (_currentExerciseId: string) => {
      if (!hasNextSlide()) return;
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(goToNextSlide, SLIDE_ADVANCE_MS);
    },
    [hasNextSlide, goToNextSlide]
  );

  // The tutor is declared above this, so it reaches the advance through a ref.
  useEffect(() => {
    advanceRef.current = advanceAfterCorrect;
  }, [advanceAfterCorrect]);

  // The wire name is `exerciseId`, and it is `ID!`. It was `problemId` until
  // „An exercise is a question" (84c4f558e) renamed every mutation document and
  // this one object literal was missed — so for a month every save from this
  // page was rejected as a null non-null variable, before any resolver ran. No
  // exception, no 500, nothing in the error log: a grown-up graded a session and
  // the questions stayed PENDING. Keep the parameter named for the wire, so the
  // two cannot drift apart again.
  function persistAnswer(params: {
    exerciseId: string;
    answer?: string;
    result?: GradeResult;
    answerRevealed?: boolean;
    manuallyGraded?: boolean;
  }) {
    checkStudentAnswer({
      variables: {
        sessionId: id,
        exerciseId: params.exerciseId,
        answer: params.answer,
        result: params.result,
        answerRevealed: params.answerRevealed,
        manuallyGraded: params.manuallyGraded,
      },
    }).catch(() => {
      toast.error("Nepavyko išsaugoti");
    });
  }

  function markAutoGraded(problemId: string) {
    setManuallyGraded((prev) => {
      if (!prev.has(problemId)) return prev;
      const next = new Set(prev);
      next.delete(problemId);
      return next;
    });
  }

  function checkProblem(problem: Exercise) {
    if (!problem.expectedAnswer) return;

    // Reading time only counts before the first attempt. Nothing has to be
    // closed here any more: the theory is only ever open because somebody
    // opened it.
    theoryView.markAttempted(problem.id);

    // What the student typed, with the displayed unit appended.
    const answerAt = (key: string) => composeAnswer(answers[key] || "", answerFormats[key]);

    // Only a clean first hit moves on by itself — anything the tutor touched
    // leaves the student in place to read what they were told.
    const onCorrect = () => {
      if (!grades[problem.id] && !tutor.discussions[problem.id]) advanceAfterCorrect(problem.id);
    };

    if (problem.answerType === "fill_blanks") {
      let expected: string[];
      try {
        expected = JSON.parse(problem.expectedAnswer);
      } catch {
        return;
      }
      const userSubs = expected.map((_, i) => answerAt(`${problem.id}__${i}`));
      if (!userSubs.some((a) => a.trim())) return;
      const { results, overall } = checkFillBlanksAdvanced(userSubs, expected, problem.answerRules);
      setSubAnswerResults((prev) => ({ ...prev, [problem.id]: results }));
      setGrades((prev) => ({ ...prev, [problem.id]: overall }));
      markAutoGraded(problem.id);
      const joined = userSubs.join("|");
      persistAnswer({ exerciseId: problem.id, answer: joined, result: overall, manuallyGraded: false });
      if (overall === "CORRECT") onCorrect();
      else if (overall === "INCORRECT" && solving) tutor.start(problem.id, joined);
      return;
    }

    const multi = detectMultiAnswers(problem.expectedAnswer, problem.problemText, problem.choices);
    if (multi) {
      const userSubs = multi.map((_, i) => answerAt(`${problem.id}__${i}`));
      if (!userSubs.some((a) => a.trim())) return;
      const { results, overall } = checkMultiAnswerAdvanced(userSubs, multi, problem.answerRules);
      setSubAnswerResults((prev) => ({ ...prev, [problem.id]: results }));
      setGrades((prev) => ({ ...prev, [problem.id]: overall }));
      markAutoGraded(problem.id);
      const joined = userSubs.join(", ");
      persistAnswer({ exerciseId: problem.id, answer: joined, result: overall, manuallyGraded: false });
      if (overall === "CORRECT") onCorrect();
      else if (overall === "INCORRECT" && solving) tutor.start(problem.id, joined);
      return;
    }

    const userAnswer = answerAt(problem.id);
    if (!userAnswer) return;
    const result = checkAnswerAdvanced(userAnswer, problem.expectedAnswer, problem.answerRules);
    setGrades((prev) => ({ ...prev, [problem.id]: result }));
    markAutoGraded(problem.id);
    persistAnswer({ exerciseId: problem.id, answer: userAnswer, result, manuallyGraded: false });
    if (result === "CORRECT") onCorrect();
    else if (result === "INCORRECT" && solving) tutor.start(problem.id, userAnswer);
  }

  function handleChoiceSelect(problem: Exercise, value: string) {
    setAnswers((prev) => ({ ...prev, [problem.id]: value }));
    if (!problem.expectedAnswer) return;
    const firstAttempt = !grades[problem.id] && !tutor.discussions[problem.id];
    const result = checkAnswerAdvanced(value, problem.expectedAnswer, problem.answerRules);
    setGrades((prev) => ({ ...prev, [problem.id]: result }));
    markAutoGraded(problem.id);
    persistAnswer({ exerciseId: problem.id, answer: value, result, manuallyGraded: false });
    if (result === "CORRECT" && firstAttempt) advanceAfterCorrect(problem.id);
    else if (result === "INCORRECT" && solving) tutor.start(problem.id, value);
  }

  function revealAnswer(problemId: string) {
    const alreadyRevealed = revealedAnswers.has(problemId);
    if (alreadyRevealed) {
      setRevealedAnswers((prev) => {
        const next = new Set(prev);
        next.delete(problemId);
        return next;
      });
      persistAnswer({ exerciseId: problemId, answerRevealed: false });
      return;
    }
    setRevealedAnswers((prev) => new Set(prev).add(problemId));
    // Revealing counts as incorrect by default — only if not already graded correct.
    const current = grades[problemId];
    const newResult: GradeResult = current || "INCORRECT";
    if (!current) {
      setGrades((prev) => ({ ...prev, [problemId]: "INCORRECT" }));
    }
    persistAnswer({
      exerciseId: problemId,
      answerRevealed: true,
      result: newResult,
    });
  }

  function setManualGrade(problemId: string, result: GradeResult) {
    setGrades((prev) => ({ ...prev, [problemId]: result }));
    setManuallyGraded((prev) => new Set(prev).add(problemId));
    persistAnswer({ exerciseId: problemId, result, manuallyGraded: true });
  }

  async function handleGenerate() {
    setGenerateOpen(false);
    try {
      const { data: genData } = await generateExercises({
        variables: {
          sessionId: id,
          // Empty field means "however long this learner's sessions are", which
          // the server resolves for itself.
          targetExerciseCount: requestedCount ?? undefined,
          maxNewSkills: overrides.maxNewSkills ?? null,
          includeDistantReview: overrides.includeDistantReview ?? null,
        },
      });

      const errors = genData?.generateExercises?.errors;
      if (errors && errors.length > 0) {
        const missingSkills: MissingSkill[] = genData?.generateExercises?.missingSkills || [];
        const noExercises = errors.some((e: string) => e.includes("No exercises available"));
        if (noExercises && missingSkills.length > 0) {
          const firstSkill = missingSkills[0];
          const skillNames = missingSkills.map((s: MissingSkill) => s.skillName).join(", ");
          toast.error(`Nėra užduočių: ${skillNames}`, {
            duration: 10000,
            action: {
              label: "Generuoti",
              onClick: () => window.open(firstSkill.generateUrl, "_blank"),
            },
          });
        } else if (noExercises) {
          toast.error(
            "Nėra patvirtintų užduočių šiam dalykui. Sugeneruokite ir patvirtinkite admin puslapyje.",
            {
              duration: 8000,
              // The browser is a development screen (`DevTools`), so offer it only
              // where it opens — a button that bounces to the dashboard reads as a
              // broken app on top of a session that would not generate.
              action: devTools
                ? {
                    label: "Eiti į admin",
                    onClick: () => {
                      const params = new URLSearchParams();
                      if (session.subjectKey) params.set("subject", session.subjectKey);
                      // The learner's own grade, which respects the year they
                      // started grade 1 where a guess from their age cannot.
                      if (session.student?.grade)
                        params.set("grade", String(session.student.grade));
                      window.open(`/admin/exercises?${params.toString()}`, "_blank");
                    },
                  }
                : undefined,
            }
          );
        } else {
          toast.error(errors.join("; "));
        }
        return;
      }

      const missingSkills: MissingSkill[] = genData?.generateExercises?.missingSkills || [];
      // A session shorter than asked for is the thing a grown-up notices and has
      // no way to explain, so it outranks the missing-content warning: the caps
      // are usually the reason, and the content gap is named among them when it
      // is not.
      const limits: PlanLimit[] = genData?.generateExercises?.limits || [];
      const generated = genData?.generateExercises?.generatedCount;
      const requested = genData?.generateExercises?.requestedCount;

      if (limits.length > 0 && generated != null && requested != null) {
        toast.warning(`Sugeneruota ${generated} iš ${requested} užduočių`, {
          description: limits.map((l) => l.message).join(" • "),
          duration: 12000,
        });
      } else if (missingSkills.length > 0) {
        const firstSkill = missingSkills[0];
        const skillNames = missingSkills.map((s: MissingSkill) => s.skillName).join(", ");
        toast.warning(`Trūksta užduočių: ${skillNames}`, {
          duration: 10000,
          action: {
            label: "Generuoti",
            onClick: () => window.open(firstSkill.generateUrl, "_blank"),
          },
        });
      } else {
        toast.success("Užduotys sugeneruotos sėkmingai");
      }
      refetch();

      if (genData?.generateExercises?.session?.hasNewSkill) {
        setShowNewSkillModal(true);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nepavyko sugeneruoti užduočių");
    }
  }

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await deleteSession({ variables: { id } });
      toast.success("Sesija ištrinta");
      navigate("/sessions");
    } catch {
      toast.error("Nepavyko ištrinti sesijos");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  }

  async function handleComplete() {
    setIsCompleting(true);
    try {
      const { data: completeData } = await completeSession({
        variables: { sessionId: id },
        refetchQueries: session
          ? [
              { query: GET_STUDENT, variables: { id: session.student.id } },
              { query: GET_STUDENT_AVATAR_CREDITS, variables: { id: session.student.id } },
            ]
          : [],
        awaitRefetchQueries: true,
      });

      const gamification = completeData?.completeSession?.gamification;
      if (gamification) {
        setGamificationResult(gamification);
        setShowCompletionSummary(true);

        const celebrationEvents = toCelebrationEvents(gamification);

        if (celebrationEvents.length > 0) {
          celebrateMultiple(celebrationEvents);
        } else {
          fireConfetti("default");
        }
      }
    } catch {
      toast.error("Nepavyko užbaigti sesijos");
    } finally {
      setIsCompleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Sesija nerasta</p>
        <Button asChild className="mt-4">
          <Link to="/sessions">Grįžti į sąrašą</Link>
        </Button>
      </div>
    );
  }

  const totalProblems = session.exercises.length;
  const gradedCount = Object.keys(grades).length;
  const correctCount = Object.values(grades).filter((g) => g === "CORRECT").length;
  const incorrectCount = Object.values(grades).filter((g) => g === "INCORRECT").length;
  const skippedCount = Object.values(grades).filter((g) => g === "SKIPPED").length;
  // A skipped task was never answered, so it is not part of what was worked
  // through. Left in the denominator it scores exactly like a wrong answer.
  const answeredProblems = totalProblems - skippedCount;

  // Completion summary view
  if (showCompletionSummary && gamificationResult) {
    const performancePercent = answeredProblems > 0
      ? Math.round((correctCount / answeredProblems) * 100)
      : 0;
    const getPerformanceMessage = () => {
      if (performancePercent >= 90) return "Nuostabus rezultatas!";
      if (performancePercent >= 70) return "Puikus darbas!";
      if (performancePercent >= 50) return "Gerai sekasi!";
      return "Praktika tobulina!";
    };

    return (
      <div className="space-y-6">
        <Card className="max-w-2xl mx-auto overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 p-1">
            <div className="bg-background p-6">
              <div className="text-center space-y-4">
                <div className="text-6xl animate-bounce">🎉</div>
                <h1 className="text-3xl font-bold">Sesija užbaigta!</h1>
                <p className="text-muted-foreground">
                  {getPerformanceMessage()}, {session.student.name}!
                </p>
                <p className="text-sm text-muted-foreground">
                  Teisingai: {correctCount} iš {answeredProblems} ({performancePercent}%)
                  {skippedCount > 0 && ` · praleista ${skippedCount}`}
                </p>
              </div>
            </div>
          </div>

          <CardContent className="p-6 space-y-6">
            <div className="text-center p-6 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="text-5xl font-bold text-amber-600 dark:text-amber-400">
                +{gamificationResult.pointsEarned}
              </div>
              <div className="text-lg text-amber-700 dark:text-amber-300 mt-1">
                taškų uždirbta
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Iš viso: {gamificationResult.newTotalPoints} taškų
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 p-4 bg-muted/50 rounded-xl">
              <span className="text-4xl">{gamificationResult.level.icon}</span>
              <div>
                <div className="text-sm text-muted-foreground">Dabartinis lygis</div>
                <div className="text-xl font-bold">
                  Lygis {gamificationResult.level.level}: {gamificationResult.level.name}
                </div>
              </div>
            </div>

            {gamificationResult.events.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-500" />
                  Pasiekimai šioje sesijoje
                </h3>
                <div className="space-y-2">
                  {gamificationResult.events.map((event, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <span className="text-2xl">
                        {event.achievementIcon ||
                          event.levelIcon ||
                          (event.type === "skill_unlocked"
                            ? "🔓"
                            : event.type === "skill_mastered"
                              ? "🏅"
                              : event.type === "personal_best"
                                ? "🎯"
                                : "⭐")}
                      </span>
                      <div className="flex-1">
                        <div className="font-medium">
                          {event.achievementName || event.message}
                        </div>
                        {event.points && event.points > 0 && (
                          <div className="text-sm text-amber-600">+{event.points} taškų</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button asChild className="flex-1">
                <Link to={`/students/${session.student.id}`}>Mokinio profilis</Link>
              </Button>
              <Button variant="outline" asChild className="flex-1">
                <Link to="/sessions">Visos sesijos</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canSolve =
    session.status === "GENERATED" || session.status === "GRADED";
  // First unanswered question — emphasized as the active one while solving
  const firstUnansweredId = canSolve
    ? session.exercises
        .filter((ex: Exercise) => ex.expectedAnswer)
        .map((ex: Exercise) => ex.id)
        .find((exerciseId: string) => !grades[exerciseId])
    : undefined;
  const canComplete =
    (session.status === "GENERATED" || session.status === "GRADED") &&
    totalProblems > 0;

  // While solving, show one exercise at a time; a completed session is reviewed
  // as a whole list.
  const slideMode = canSolve && session.exercises.length > 1;
  const slideIndex = Math.min(currentSlide, Math.max(0, session.exercises.length - 1));
  const visibleExercises: Exercise[] = slideMode
    ? [session.exercises[slideIndex]]
    : session.exercises;
  // An open discussion (or a running AI review) holds the student on this slide.
  // The question whose theory is open in the side panel.
  const theoryExercise: Exercise | undefined = theoryFor
    ? session.exercises.find((ex: Exercise) => ex.id === theoryFor)
    : undefined;
  const slideBlocked = slideMode ? tutor.blocks(session.exercises[slideIndex]) : false;
  const anyOpenDiscussion = tutor.anyOpen;

  // Details start expanded only while there is nothing generated yet.
  const showDetails = detailsOpen ?? session.exercises.length === 0;

  // Aggregate status of an exercise, for the progress rail.
  function exerciseStatus(
    exercise: Exercise
  ): "pending" | "correct" | "partial" | "incorrect" {
    if (!exercise.expectedAnswer) return "pending";
    const result = grades[exercise.id];
    if (!result) return "pending";
    if (result === "CORRECT") return "correct";
    if (result === "INCORRECT") return "incorrect";
    return "partial";
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Compact bar — always visible; expands to the full session details */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/sessions">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <button
          type="button"
          onClick={() => setDetailsOpen(!showDetails)}
          className="flex flex-1 items-center gap-2 flex-wrap text-left cursor-pointer min-w-0"
          aria-expanded={showDetails}
        >
          {showDetails ? (
            <span className="flex-1 text-sm font-medium text-muted-foreground">
              Sesijos informacija
            </span>
          ) : (
            <>
              <span className="font-semibold truncate">{session.student.name}</span>
              {getStatusBadge(session.status)}
              {session.subject && (
                <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                  <SubjectIcon icon={session.subject.icon} className="h-4 w-4" />
                  {session.subject.name}
                </span>
              )}
              {session.exercises.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {gradedCount} / {totalProblems}
                  {correctCount > 0 && (
                    <span className="ml-1.5 text-green-600 dark:text-green-400">
                      {correctCount} teisingai
                    </span>
                  )}
                </span>
              )}
            </>
          )}
          {showDetails ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </div>

      {showDetails && (
        <>
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight">{session.student.name}</h1>
                {getStatusBadge(session.status)}
                {session.customConfig && (
                  <Badge variant="outline" className="flex items-center gap-1">
                    <Settings2 className="h-3 w-3" />
                    Individuali
                  </Badge>
                )}
                {personalizationBadge && (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400"
                    title={session.personalizationProfile || undefined}
                  >
                    <Sparkles className="h-3 w-3" />
                    {personalizationBadge}
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground flex items-center gap-1">
                {session.subject && (
                  <span className="inline-flex items-center gap-1 font-medium">
                    <SubjectIcon icon={session.subject.icon} className="h-4 w-4" />
                    {session.subject.name} ·{" "}
                  </span>
                )}
                {new Date(session.createdAt).toLocaleDateString("lt-LT", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="icon">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ištrinti sesiją</DialogTitle>
                  <DialogDescription>
                    Ar tikrai norite ištrinti šią sesiją? Šis veiksmas negrįžtamas.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                    Atšaukti
                  </Button>
                  <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                    {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Ištrinti
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Actions. Everything that takes a decision — how long the session
              is, which planner rules to lift, how to reword it — sits behind
              its own button: those are occasional choices, and laid out flat
              they were a row of controls above every visit that only wanted to
              read the questions. */}
          <div className="flex flex-wrap items-center gap-3" data-testid="session-actions">
            {canGenerate && (
              <Dialog
                open={generateOpen}
                onOpenChange={(next) => {
                  setGenerateOpen(next);
                  // A session that already has questions starts from its own
                  // length: "regenerate" usually means this session again, and
                  // silently resetting it to the learner's default would be a
                  // change nobody asked for.
                  if (next && countDraft === null && session.exercises.length > 0) {
                    setCountDraft(String(session.exercises.length));
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    variant={session.status === "DRAFT" ? "default" : "outline"}
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    {isGenerating
                      ? "Generuojama..."
                      : session.status === "DRAFT"
                        ? "Generuoti užduotis"
                        : "Pergeneruoti"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {session.status === "DRAFT" ? "Generuoti užduotis" : "Pergeneruoti užduotis"}
                    </DialogTitle>
                    <DialogDescription>
                      {session.status === "DRAFT"
                        ? "Kokia bus ši sesija."
                        : "Dabartinės užduotys bus pakeistos naujomis — atsakymai neišsaugomi."}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-5">
                    <ExerciseCountField
                      draft={countDraft}
                      onDraftChange={setCountDraft}
                      suggested={suggestedExerciseCount}
                    />

                    <div className="space-y-2">
                      <Label htmlFor="max-new-skills">Naujos temos</Label>
                      <Select
                        value={String(overrides.maxNewSkills ?? 1)}
                        onValueChange={(value) =>
                          setOverrides((prev) => ({ ...prev, maxNewSkills: Number(value) }))
                        }
                      >
                        <SelectTrigger id="max-new-skills" className="w-[160px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5].map((option) => (
                            <SelectItem key={option} value={String(option)}>
                              {option === 1 ? "1 (įprasta)" : option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* What this length will actually come out as, while the
                        choice is still being made */}
                    {forecast && (
                      <SessionLengthForecast
                        requested={forecast.requestedCount}
                        planned={forecast.plannedCount}
                        limits={forecast.limits}
                        overrides={overrides}
                        onApply={(next) => setOverrides((prev) => ({ ...prev, ...next }))}
                        onReset={() => setOverrides({})}
                      />
                    )}
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setGenerateOpen(false)}>
                      Atšaukti
                    </Button>
                    <Button onClick={handleGenerate} disabled={isGenerating}>
                      {isGenerating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="mr-2 h-4 w-4" />
                      )}
                      {session.status === "DRAFT" ? "Generuoti" : "Pergeneruoti"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Rewording the questions around the learner: its own dialog */}
            {session.status === "GENERATED" && session.exercises.length > 0 && (
              <SessionPersonalization
                sessionId={session.id}
                status={session.personalizationStatus}
                personalizedCount={session.personalizedProblemCount}
                totalProblems={totalProblems}
                sessionProfile={session.personalizationProfile}
                onDone={() => refetch()}
              />
            )}

            {(session.status === "GENERATED" ||
              session.status === "GRADED" ||
              session.status === "COMPLETED") && (
              <Button variant="outline" asChild>
                <Link to={`/sessions/${id}/print`}>
                  <Printer className="mr-2 h-4 w-4" />
                  Spausdinti
                </Link>
              </Button>
            )}
          </div>

          {/* Session Plan Preview */}
          {session.sessionPlan && session.sessionPlan.length > 0 && session.status === "GENERATED" && (
            <SessionPlanPreview plan={session.sessionPlan} />
          )}

          {/* Stats */}
          {session.exercises.length > 0 && (session.status === "GENERATED" || session.status === "GRADED") && (
            <Card>
              <CardContent className="py-4">
                <div className="flex gap-6 text-sm flex-wrap">
                  <div>
                    Įvertinta: <strong>{gradedCount}</strong> / {totalProblems}
                  </div>
                  <div className="text-green-600 dark:text-green-400">
                    Teisingai: <strong>{correctCount}</strong>
                  </div>
                  <div className="text-red-600 dark:text-red-400">
                    Neteisingai: <strong>{incorrectCount}</strong>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </>
      )}

      {/* The theory, beside the question rather than on top of it. A theory file
          here runs to 16–23 KB, so unfolding one inline pushes the question, the
          answer and the correction off the screen — the defect already fixed on
          the learner's side. In a panel the question stays where it was and the
          material scrolls on its own. */}
      <Sheet
        open={!!theoryExercise}
        onOpenChange={(open) => {
          if (!open) setTheoryFor(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl lg:max-w-2xl gap-0 p-0"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-muted-foreground" />
              Teorija
              {theoryExercise?.category === "new" && (
                <span className="text-sm font-normal text-cyan-600 dark:text-cyan-400">
                  (nauja tema)
                </span>
              )}
            </SheetTitle>
            <SheetDescription className="flex items-center justify-between gap-3">
              <span>{theoryExercise?.skill?.name}</span>
              {theoryExercise && (
                <Button variant="outline" size="sm" asChild>
                  {/* A new tab, because printing is a detour and the session is
                      still open behind it. */}
                  <a
                    href={`/sessions/${id}/theory/print?skill=${encodeURIComponent(
                      theoryExercise.skillKey
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    Spausdinti
                  </a>
                </Button>
              )}
            </SheetDescription>
          </SheetHeader>
          <div
            className="flex-1 overflow-y-auto px-4 py-4"
            ref={(node) => {
              if (theoryExercise) theoryView.attach(theoryExercise.id, node, false);
            }}
          >
            {theoryExercise?.theory && (
              <TheoryView html={theoryExercise.theory} className="text-sm" />
            )}
          </div>
          {canSolve && theoryExercise && (
            <SheetFooter className="border-t flex-row justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  theoryView.markAcknowledged(theoryExercise.id);
                  setTheoryFor(null);
                }}
              >
                <Check className="h-4 w-4 mr-1" />
                Supratau
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* New Skill Modal */}
      {session.newSkillKey && (
        <NewSkillModal
          isOpen={showNewSkillModal}
          onClose={() => setShowNewSkillModal(false)}
          skillName={
            session.sessionPlan?.find((p: SessionPlanEntry) => p.category === "new")?.skillName ||
            session.newSkillKey
          }
        />
      )}

      {/* Exercises */}
      {session.exercises.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium">Nėra užduočių</p>
            <p className="text-sm text-muted-foreground mb-4">
              Paspauskite &quot;Generuoti užduotis&quot;, kad AI sukurtų užduotis mokiniui
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Progress rail — one dot per exercise, click to jump */}
          {slideMode && (
            <div className="flex flex-wrap items-center justify-center gap-1.5 pb-2">
              {session.exercises.map((ex: Exercise, i: number) => {
                const status = exerciseStatus(ex);
                const color =
                  status === "correct"
                    ? "bg-green-500"
                    : status === "incorrect"
                      ? "bg-red-500"
                      : status === "partial"
                        ? "bg-yellow-500"
                        : "bg-muted-foreground/30";
                return (
                  <button
                    key={ex.id}
                    type="button"
                    aria-label={`Užduotis ${i + 1}`}
                    onClick={() => setCurrentSlide(i)}
                    className={`h-2.5 rounded-full transition-all ${color} ${
                      i === slideIndex
                        ? "w-8 ring-2 ring-primary ring-offset-2 ring-offset-background"
                        : "w-2.5"
                    }`}
                  />
                );
              })}
            </div>
          )}

          {(() => {
            const nodes: React.ReactNode[] = [];
            // Numbering stays global so the rail and the task numbers agree.
            let globalIndex = slideMode ? slideIndex : 0;
            visibleExercises.forEach((exercise: Exercise) => {
              {
                const problem = exercise;
                globalIndex += 1;
                // Whether the chat's own "move on" button has anywhere to lead;
                // the panel decides when to show it (answer accepted, or over).
                const canLeaveAfterDiscussion = !!tutor.discussions[problem.id] && hasNextSlide();
                nodes.push(
                  <ProblemRow
                    key={problem.id}
                    problem={problem}
                    index={globalIndex - 1}
                    session={session}
                    canSolve={canSolve}
                    isActive={problem.id === firstUnansweredId}
                    answers={answers}
                    setAnswers={setAnswers}
                    grades={grades}
                    revealedAnswers={revealedAnswers}
                    revealedHints={revealedHints}
                    setRevealedHints={setRevealedHints}
                    manuallyGraded={manuallyGraded}
                    subAnswerResults={subAnswerResults}
                    inputRefs={inputRefs}
                    discussion={tutor.discussions[problem.id]}
                    adjudicating={tutor.isAdjudicating(problem.id)}
                    sendingDiscussion={tutor.isSending(problem.id)}
                    onCheck={() => checkProblem(problem)}
                    onChoiceSelect={(v) => handleChoiceSelect(problem, v)}
                    onReveal={() => revealAnswer(problem.id)}
                    onManualGrade={(r) => setManualGrade(problem.id, r)}
                    onOpenTheory={problem.theory ? () => setTheoryFor(problem.id) : undefined}
                    onSendDiscussion={(message) => tutor.send(problem.id, message)}
                    onResolveDiscussion={(skipped) => tutor.resolve(problem.id, skipped)}
                    onNextAfterDiscussion={canLeaveAfterDiscussion ? goToNextSlide : undefined}
                    nextLabel="Kita užduotis"
                  />
                );
              }
            });
            return nodes;
          })()}

        </div>
      )}

      {/* Skill Changes Summary (completed sessions) */}
      {session.status === "COMPLETED" && (session.skillChanges?.length ?? 0) > 0 && (
        <Card className="mt-6 border-primary/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Sesijos rezultatai
            </CardTitle>
            <CardDescription>
              Šios sesijos rezultatai ir jų įtaka įgūdžių vertinimui
            </CardDescription>
          </CardHeader>
          <CardContent>
            {session.sessionPlan && session.sessionPlan.length > 0 ? (
              <SessionPlanSummary
                plan={session.sessionPlan}
                skillChanges={session.skillChanges}
              />
            ) : (
              <div className="space-y-3">
                {session.skillChanges.map((sc: SkillChange) => (
                  <div
                    key={sc.skillKey}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{sc.skillName}</p>
                      <p className="text-sm text-muted-foreground">
                        {sc.oldProficiency.toFixed(1)}% → {sc.newProficiency.toFixed(1)}%
                      </p>
                    </div>
                    <SkillChangeIndicator change={sc.change} />
                  </div>
                ))}
              </div>
            )}
            {session.pointsEarned > 0 && (
              <PointsBreakdown
                pointsEarned={session.pointsEarned}
                events={(session.gamificationBreakdown as GamificationEvent[] | null) || []}
                totalProblems={totalProblems}
                correctCount={correctCount}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Sticky bar: slide navigation always within reach, plus completion */}
      {(slideMode || canComplete) && (
        <div className="fixed bottom-0 left-0 right-0 p-3 bg-background border-t shadow-lg z-10">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            {slideMode ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentSlide((s) => Math.max(0, s - 1))}
                  disabled={slideIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Atgal
                </Button>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {slideIndex + 1} / {session.exercises.length}
                </span>
                <Button
                  size="sm"
                  onClick={() =>
                    setCurrentSlide((s) => Math.min(session.exercises.length - 1, s + 1))
                  }
                  disabled={slideIndex >= session.exercises.length - 1 || slideBlocked}
                  title={slideBlocked ? "Pirmiausia aptark šią užduotį pokalbyje" : undefined}
                >
                  Toliau
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                Įvertinta: {gradedCount} / {totalProblems}
                {gradedCount > 0 && (
                  <span className="ml-2 text-green-600 dark:text-green-400">
                    {correctCount} teisingai
                  </span>
                )}
              </span>
            )}

            {canComplete && (
              <div className="flex items-center gap-3 flex-wrap">
                {slideMode && (
                  <span className="text-sm text-muted-foreground">
                    Įvertinta: {gradedCount} / {totalProblems}
                    {gradedCount > 0 && (
                      <span className="ml-2 text-green-600 dark:text-green-400">
                        {correctCount} teisingai
                      </span>
                    )}
                  </span>
                )}
                <Button
                  onClick={handleComplete}
                  disabled={isCompleting || gradedCount !== totalProblems || anyOpenDiscussion}
                  title={anyOpenDiscussion ? "Pirmiausia užbaik neužbaigtą pokalbį" : undefined}
                  size="lg"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {isCompleting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trophy className="mr-2 h-4 w-4" />
                  )}
                  Užbaigti ir gauti taškus!
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact time spent on a single problem. Keeps second-level precision since
// per-task times are short (a slow task might be ~2 min).
function formatProblemTime(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return `${minutes}:${rem.toString().padStart(2, "0")}`;
}

type ProblemRowProps = {
  problem: Exercise;
  index: number;
  session: { status: string; skillChanges: SkillChange[] };
  canSolve: boolean;
  isActive: boolean;
  answers: Record<string, string>;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  grades: Record<string, GradeResult>;
  revealedAnswers: Set<string>;
  revealedHints: Set<string>;
  setRevealedHints: React.Dispatch<React.SetStateAction<Set<string>>>;
  manuallyGraded: Set<string>;
  subAnswerResults: Record<string, CheckResult[]>;
  inputRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  discussion?: Discussion;
  adjudicating?: boolean;
  sendingDiscussion?: boolean;
  onCheck: () => void;
  onChoiceSelect: (value: string) => void;
  onReveal: () => void;
  onManualGrade: (result: GradeResult) => void;
  /** Open this question's theory in the side panel. Absent when the skill has
   *  no `theory.html` — 134 of the skills with questions have none. */
  onOpenTheory?: () => void;
  onSendDiscussion?: (message: string) => void;
  onResolveDiscussion?: (skipped: boolean) => void;
  onNextAfterDiscussion?: () => void;
  nextLabel?: string;
};

function ProblemRow({
  problem,
  index,
  session,
  canSolve,
  isActive,
  answers,
  setAnswers,
  grades,
  revealedAnswers,
  revealedHints,
  setRevealedHints,
  manuallyGraded,
  subAnswerResults,
  inputRefs,
  discussion,
  adjudicating,
  sendingDiscussion,
  onCheck,
  onChoiceSelect,
  onReveal,
  onManualGrade,
  onOpenTheory,
  onSendDiscussion,
  onResolveDiscussion,
  onNextAfterDiscussion,
  nextLabel,
}: ProblemRowProps) {
  const result = grades[problem.id];
  const isCorrect = result === "CORRECT";
  const isIncorrect = result === "INCORRECT";
  const isPartial = result === "PARTIAL";
  const isSkipped = result === "SKIPPED";
  const isRevealed = revealedAnswers.has(problem.id);

  const answerFormats = useMemo(() => buildAnswerFormats(problem), [problem]);

  const isFillBlanks = problem.answerType === "fill_blanks";
  const fillBlanksSegments = isFillBlanks ? splitFillBlanks(problem.problemText) : null;
  const fillBlanksCount = isFillBlanks
    ? fillBlanksSegments!.filter((s) => s.type === "blank").length
    : 0;
  const isChoice = !isFillBlanks && isChoiceProblem(problem.choices);
  const multiAnswers =
    !isFillBlanks && problem.expectedAnswer
      ? detectMultiAnswers(problem.expectedAnswer, problem.problemText, problem.choices)
      : null;
  const isMulti = !!multiAnswers;

  const hasAnswer = isFillBlanks
    ? Array.from({ length: fillBlanksCount }, (_, i) =>
        answers[`${problem.id}__${i}`]?.trim()
      ).some(Boolean)
    : isMulti
      ? multiAnswers!.some((_, i) => answers[`${problem.id}__${i}`]?.trim())
      : !!answers[problem.id]?.trim();

  const subResults = subAnswerResults[problem.id];
  const skillChange =
    session.status === "COMPLETED" && problem.skillKey
      ? session.skillChanges?.find((sc: SkillChange) => sc.skillKey === problem.skillKey)
      : null;

  const expectedDisplay = problem.expectedAnswer
    ? problem.expectedAnswer.split(";;")[0]
    : null;

  const hintRevealed = revealedHints.has(problem.id);
  const isManual = manuallyGraded.has(problem.id);
  const graded = !!result;
  const hasOpenDiscussion = discussion?.status === "OPEN";
  // Keep the answer hidden while the AI is reviewing it or tutoring the student.
  const suppressAnswer = !!adjudicating || hasOpenDiscussion;
  const showExpectedInline =
    graded && expectedDisplay && (!isCorrect || !canSolve) && !suppressAnswer;
  const userAnswerDisplay = (() => {
    if (!problem.userAnswer) return null;
    if (problem.answerType === "fill_blanks") {
      return problem.userAnswer.split("|").join(", ");
    }
    return problem.userAnswer;
  })();
  const showReadOnlyAnswer =
    !canSolve && !isChoice && !isFillBlanks && !isMulti && (userAnswerDisplay || isSkipped);

  return (
    <div
      className={cn(
        "rounded-lg border transition-all",
        !graded && isActive ? "p-6" : "p-4",
        !graded && isActive && "exercise-active scroll-mt-20 border-2 border-primary ring-2 ring-primary/30",
        isCorrect && "border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20",
        isIncorrect && "border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20",
        isPartial && "border-yellow-200 bg-yellow-50/50 dark:border-yellow-900 dark:bg-yellow-950/20",
        isSkipped && "border-gray-200 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-900/20"
      )}
    >
      {/* Top meta row: skill name, category badge, action icons */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          <span className="font-medium text-foreground/70">{index + 1}.</span>
          {problem.skill?.name && <span>{problem.skill.name}</span>}
          {problem.category && (
            <SkillCategoryBadge
              category={problem.category as "new" | "struggling" | "learning" | "review"}
              size="sm"
            />
          )}
          {skillChange && <SkillChangeIndicator change={skillChange.change} />}
          {problem.activeSeconds > 0 && (
            <span
              className="inline-flex items-center gap-1"
              title="Laikas, praleistas prie šios užduoties"
            >
              <Clock className="h-3 w-3" />
              {formatProblemTime(problem.activeSeconds)}
            </span>
          )}
          {/* Why this question is yellow. PARTIAL means „partly right" on a
              multi-blank question and „right but too slow" here, and the grade
              alone cannot say which — the same reason the child gets a note.

              The two numbers together are the whole point: „14 s (riba 10 s)"
              says how far over, which is what decides whether the child is
              close to fluent or nowhere near, and it is also the evidence for
              re-tuning the limit if a question turns out to be unfair. */}
          {problem.answeredLate && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 font-medium text-yellow-800 dark:text-yellow-200"
              title={
                problem.timeLimit
                  ? `Atsakė teisingai, bet per ${problem.answerSeconds} s — riba ${problem.timeLimit} s. ` +
                    "Šio įgūdžio esmė yra greitis, todėl užduotis pasikartos."
                  : "Atsakė teisingai, bet nespėjo laiku"
              }
            >
              <Timer className="h-3 w-3" />
              {problem.answerSeconds && problem.timeLimit
                ? `Nespėjo (${problem.answerSeconds} s / ${problem.timeLimit} s)`
                : "Nespėjo"}
            </span>
          )}

          {/* Answered inside the limit — the thing worth knowing about a
              fluency question, and invisible in a green tick that looks
              identical to a green tick on a word problem. Quiet, because it is
              the ordinary good outcome rather than something to act on. */}
          {!problem.answeredLate && problem.timeLimit && problem.answerSeconds != null && (
            <span
              className="inline-flex items-center gap-1"
              title={`Atsakė per ${problem.answerSeconds} s, riba ${problem.timeLimit} s`}
            >
              <Timer className="h-3 w-3" />
              {problem.answerSeconds} s / {problem.timeLimit} s
            </span>
          )}

          {problem.discussion?.solvedInChat && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 font-medium text-yellow-800 dark:text-yellow-200"
              title={
                problem.discussion.retryCorrect
                  ? "Pirmas atsakymas buvo neteisingas, antras – teisingas"
                  : "Teisingą atsakymą rado pokalbio metu"
              }
            >
              <GraduationCap className="h-3 w-3" />
              {problem.discussion.retryCorrect ? "Iš antro karto" : "Su pagalba"}
            </span>
          )}
          {problem.discussion && !problem.discussion.solvedInChat && (
            <span
              className="inline-flex items-center gap-1"
              title="Su mokiniu buvo aptarta ši užduotis"
            >
              <GraduationCap className="h-3 w-3" />
              Aptarta
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* The reads sit here as icons; the writes stay behind the three dots
              below. Theory belongs beside the hint for that reason, and because
              this cluster is the only control on the row a finished session
              still draws — the menu is gated on the session being solvable, and
              reviewing afterwards is exactly when a grown-up reaches for the
              material. */}
          {onOpenTheory && (
            <button
              type="button"
              onClick={onOpenTheory}
              title="Skaityti teoriją"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 transition-colors"
            >
              <BookOpen className="h-4 w-4" />
            </button>
          )}
          {problem.hint && (
            <button
              type="button"
              onClick={() =>
                setRevealedHints((prev) => {
                  const next = new Set(prev);
                  if (next.has(problem.id)) next.delete(problem.id);
                  else next.add(problem.id);
                  return next;
                })
              }
              title={hintRevealed ? "Slėpti užuominą" : "Rodyti užuominą"}
              className={cn(
                "inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors",
                hintRevealed && "text-amber-600 bg-amber-50 dark:bg-amber-950/30"
              )}
            >
              <Lightbulb className="h-4 w-4" />
            </button>
          )}
          {isManual && graded && (
            <span
              title="Vertinimas nustatytas rankiniu būdu"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            >
              <PenLine className="h-3.5 w-3.5" />
              rankiniu būdu
            </span>
          )}
          {/* Grading actions live behind the three dots — and stay out of reach
              while the AI is tutoring, so correctness isn't overridden mid-chat. */}
          {canSolve && !suppressAnswer && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  title="Užduoties veiksmai"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  onClick={() => {
                    if (isSkipped || window.confirm("Pažymėti užduotį kaip praleistą?")) {
                      onManualGrade("SKIPPED");
                    }
                  }}
                >
                  <SkipForward className="mr-2 h-4 w-4" />
                  Praleisti užduotį
                  {isSkipped && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Vertinimas</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => onManualGrade("CORRECT")}>
                  <Check className="mr-2 h-4 w-4 text-green-600 dark:text-green-400" />
                  Teisingai
                  {isCorrect && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onManualGrade("INCORRECT")}>
                  <X className="mr-2 h-4 w-4 text-red-600 dark:text-red-400" />
                  Neteisingai
                  {isIncorrect && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onManualGrade("PARTIAL")}>
                  <Minus className="mr-2 h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  Iš dalies
                  {isPartial && <Check className="ml-auto h-4 w-4" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3">
        <span className="font-medium text-muted-foreground flex items-center gap-1 pt-0.5">
          {result &&
            (isCorrect ? (
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
            ) : isPartial ? (
              <Minus className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            ) : isSkipped ? (
              <SkipForward className="h-4 w-4 text-gray-500" />
            ) : (
              <X className="h-4 w-4 text-red-600 dark:text-red-400" />
            ))}
        </span>
        <div className="flex-1 space-y-2">
          {/* Problem text / choice / fill-blanks */}
          {isFillBlanks ? (
            <div className="font-medium">
              {(() => {
                let blankIdx = 0;
                return fillBlanksSegments!.map((segment, segIdx) => {
                  if (segment.type === "text") {
                    return (
                      <MathContent key={segIdx} html={segment.value} className="inline" />
                    );
                  }
                  const bi = blankIdx++;
                  const subKey = `${problem.id}__${bi}`;
                  const subResult = subResults?.[bi];
                  const subChecked = !!result && subResult !== undefined;
                  const subCorrect = subResult === "CORRECT";
                  return (
                    <span key={segIdx} className="inline-flex items-center mx-0.5">
                      <input
                        ref={(el) => {
                          if (el) {
                            inputRefs.current.set(subKey, el);
                            if (bi === 0) inputRefs.current.set(problem.id, el);
                          } else {
                            inputRefs.current.delete(subKey);
                            if (bi === 0) inputRefs.current.delete(problem.id);
                          }
                        }}
                        type="text"
                        value={answers[subKey] || ""}
                        disabled={!canSolve}
                        onChange={(e) =>
                          setAnswers((prev) => ({ ...prev, [subKey]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            if (bi < fillBlanksCount - 1) {
                              const nextKey = `${problem.id}__${bi + 1}`;
                              const nextInput = inputRefs.current.get(nextKey);
                              if (nextInput) nextInput.focus();
                            } else if (hasAnswer) {
                              onCheck();
                            }
                          }
                        }}
                        style={{ width: `${Math.max(2, segment.length + 1)}ch` }}
                        className={cn(
                          "inline-block text-center border-b-2 border-t-0 border-x-0 bg-transparent outline-none px-0.5 font-medium",
                          subChecked
                            ? subCorrect
                              ? "border-green-500 text-green-700 dark:text-green-400"
                              : "border-red-500 text-red-700 dark:text-red-400"
                            : "border-gray-400 dark:border-gray-500"
                        )}
                      />
                      <AnswerAffix value={answers[subKey] || ""} format={answerFormats[subKey]} />
                      {subChecked && !subCorrect && (
                        <span className="text-xs text-green-700 dark:text-green-400 ml-1">
                          (
                          {(() => {
                            try {
                              return JSON.parse(problem.expectedAnswer!)[bi];
                            } catch {
                              return "";
                            }
                          })()}
                          )
                        </span>
                      )}
                    </span>
                  );
                });
              })()}
            </div>
          ) : isChoice ? (
            <ChoiceProblem
              questionHtml={problem.problemText}
              choices={problem.choices!}
              className="font-medium"
              zoomableFigures
              selectedAnswer={answers[problem.id]}
              disabled={!canSolve || !!result}
              onSelectAnswer={canSolve ? onChoiceSelect : undefined}
              firstButtonRef={(el) => {
                if (el) inputRefs.current.set(problem.id, el);
                else inputRefs.current.delete(problem.id);
              }}
            />
          ) : (
            <MathContent html={problem.problemText ?? ""} className="font-medium" zoomableFigures />
          )}

          {/* Multi-answer inputs */}
          {isMulti && problem.expectedAnswer && !isChoice && (
            <div className="mt-3 space-y-2">
              {multiAnswers!.map((expected, i) => {
                const subKey = `${problem.id}__${i}`;
                const subResult = subResults?.[i];
                const subChecked = !!result && subResult !== undefined;
                const subCorrect = subResult === "CORRECT";
                const label = String.fromCharCode(97 + i) + ")";
                return (
                  <div key={subKey} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground w-6">
                      {label}
                    </span>
                    <AnswerInput
                      ref={(el) => {
                        if (el) {
                          inputRefs.current.set(subKey, el);
                          if (i === 0) inputRefs.current.set(problem.id, el);
                        } else {
                          inputRefs.current.delete(subKey);
                          if (i === 0) inputRefs.current.delete(problem.id);
                        }
                      }}
                      placeholder={`${label} atsakymas...`}
                      value={answers[subKey] || ""}
                      format={answerFormats[subKey]}
                      disabled={!canSolve}
                      onValueChange={(value) =>
                        setAnswers((prev) => ({ ...prev, [subKey]: value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (i < multiAnswers!.length - 1) {
                            const nextKey = `${problem.id}__${i + 1}`;
                            const nextInput = inputRefs.current.get(nextKey);
                            if (nextInput) nextInput.focus();
                          } else if (hasAnswer) {
                            onCheck();
                          }
                        }
                      }}
                      className={cn(
                        "max-w-xs",
                        subChecked
                          ? subCorrect
                            ? "border-green-400 dark:border-green-700"
                            : "border-red-400 dark:border-red-700"
                          : ""
                      )}
                    />
                    {subChecked &&
                      (subCorrect ? (
                        <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                      ) : (
                        <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                      ))}
                    {subChecked && !subCorrect && (
                      <span className="text-sm text-green-700 dark:text-green-400">
                        → {expected}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Read-only answer display (for completed/non-solvable sessions) */}
          {showReadOnlyAnswer && (
            <div className="mt-3 space-y-1">
              {isSkipped ? (
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 dark:text-gray-400">
                  <SkipForward className="h-4 w-4" />
                  Praleista
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span className="text-muted-foreground">Pateiktas atsakymas:</span>
                  <MathContent
                    html={`<strong>${userAnswerDisplay}</strong>`}
                    className={cn(
                      "inline",
                      isCorrect && "text-green-700 dark:text-green-400",
                      isIncorrect && "text-red-700 dark:text-red-400",
                      isPartial && "text-yellow-700 dark:text-yellow-400"
                    )}
                  />
                </div>
              )}
            </div>
          )}

          {/* Single answer input */}
          {problem.expectedAnswer && !isChoice && !isMulti && !isFillBlanks && canSolve && (
            <div className="mt-3 flex items-center gap-2">
              <AnswerInput
                ref={(el) => {
                  if (el) {
                    inputRefs.current.set(problem.id, el);
                  } else {
                    inputRefs.current.delete(problem.id);
                  }
                }}
                placeholder="Įrašyk atsakymą..."
                value={answers[problem.id] || ""}
                format={answerFormats[problem.id]}
                onValueChange={(value) =>
                  setAnswers((prev) => ({ ...prev, [problem.id]: value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && hasAnswer && !suppressAnswer) onCheck();
                }}
                className={cn(
                  "max-w-xs",
                  isCorrect && "border-green-400 dark:border-green-700",
                  isIncorrect && "border-red-400 dark:border-red-700"
                )}
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={!hasAnswer || suppressAnswer}
                onClick={onCheck}
              >
                <Check className="h-4 w-4 mr-1" />
                Tikrinti
              </Button>
            </div>
          )}

          {/* Check button for multi-answer / fill-blanks */}
          {(isMulti || isFillBlanks) && canSolve && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={!hasAnswer || suppressAnswer}
                onClick={onCheck}
              >
                <Check className="h-4 w-4 mr-1" />
                Tikrinti
              </Button>
              {isPartial && subResults && (
                <span className="text-yellow-600 text-sm font-medium">
                  Iš dalies ({subResults.filter((r) => r === "CORRECT").length}/
                  {subResults.length})
                </span>
              )}
            </div>
          )}

          {/* Expected answer — shown inline after grading (incorrect/partial/skipped) */}
          {showExpectedInline && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              <span>Teisingas atsakymas:</span>
              <MathContent
                html={`<strong>${expectedDisplay}</strong>`}
                className="inline text-foreground"
              />
              {isRevealed && (
                <Badge variant="secondary" className="text-xs">
                  atskleistas
                </Badge>
              )}
            </div>
          )}

          {/* AI reviewing the answer */}
          {adjudicating && (
            <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400">
              <Sparkles className="h-4 w-4 animate-pulse" />
              Tikriname tavo atsakymą...
            </div>
          )}

          {/* Tutoring chat after a wrong answer */}
          {discussion && onSendDiscussion && onResolveDiscussion && (
            <DiscussionPanel
              discussion={discussion}
              sending={!!sendingDiscussion}
              onSend={onSendDiscussion}
              onResolve={onResolveDiscussion}
              onNext={onNextAfterDiscussion}
              nextLabel={nextLabel}
            />
          )}

          {/* Reveal answer — only when not graded yet and the AI isn't tutoring */}
          {problem.expectedAnswer && !graded && canSolve && !suppressAnswer && (
            <div>
              <button
                onClick={onReveal}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isRevealed ? (
                  <>
                    <EyeOff className="h-3.5 w-3.5" />
                    Slėpti atsakymą
                  </>
                ) : (
                  <>
                    <Eye className="h-3.5 w-3.5" />
                    Rodyti atsakymą
                  </>
                )}
              </button>
              {isRevealed && expectedDisplay && (
                <div className="mt-1 text-sm text-muted-foreground inline-flex items-center gap-2">
                  <MathContent
                    html={`<strong>${expectedDisplay}</strong>`}
                    className="inline"
                  />
                  <Badge variant="secondary" className="text-xs">
                    atskleistas
                  </Badge>
                </div>
              )}
            </div>
          )}

          {/* What the child was shown after answering a concept introduction.
              Rendered here too rather than left to the learner's page, because
              this is the grown-up's copy of the same question and the two drifting
              apart is what `.exercise-active` already cost once — a grown-up
              reviewing a session would otherwise see the options and never the
              explanation the child read.

              Not gated on the hint's reveal toggle: this is not a hint, and it is
              what the child has already seen. */}
          {problem.reveal && (
            <div className="p-3 rounded bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                <div className="intro-reveal text-sm text-indigo-900 dark:text-indigo-100">
                  <MathContent html={problem.reveal} />
                </div>
              </div>
            </div>
          )}

          {/* Hint - revealed only via icon at top */}
          {hintRevealed && problem.hint && (
            <div className="p-2 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <span className="font-medium">Užuomina:</span>{" "}
                  <MathContent html={problem.hint} className="inline" />
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

type PointEventVisual = {
  icon: React.ReactNode;
  label: string;
  detail?: string | null;
  bg: string;
  ring: string;
  iconColor: string;
};

function describeEvent(event: GamificationEvent): PointEventVisual {
  const baseClass = "h-4 w-4";
  switch (event.type) {
    case "base":
      return {
        icon: <Target className={baseClass} />,
        label: "Sesijos užbaigimas",
        detail: event.message && event.message !== "Sesijos užbaigimas" ? event.message : null,
        bg: "bg-sky-100 dark:bg-sky-950/40",
        ring: "ring-sky-200 dark:ring-sky-900",
        iconColor: "text-sky-600 dark:text-sky-400",
      };
    case "correct_answers":
      return {
        icon: <Check className={baseClass} />,
        label: "Teisingi atsakymai",
        detail: event.message?.replace(/^Teisingi atsakymai\s*/, "")?.replace(/[()]/g, "") || null,
        bg: "bg-emerald-100 dark:bg-emerald-950/40",
        ring: "ring-emerald-200 dark:ring-emerald-900",
        iconColor: "text-emerald-600 dark:text-emerald-400",
      };
    case "perfect_session":
      return {
        icon: <span className="text-base leading-none">💯</span>,
        label: "Tobula sesija",
        detail: "Visi atsakymai teisingi",
        bg: "bg-amber-100 dark:bg-amber-950/40",
        ring: "ring-amber-200 dark:ring-amber-900",
        iconColor: "text-amber-600 dark:text-amber-400",
      };
    case "skill_mastered":
      return {
        icon: <Award className={baseClass} />,
        label: "Įgūdis įvaldytas",
        detail: event.skillName || null,
        bg: "bg-fuchsia-100 dark:bg-fuchsia-950/40",
        ring: "ring-fuchsia-200 dark:ring-fuchsia-900",
        iconColor: "text-fuchsia-600 dark:text-fuchsia-400",
      };
    case "skill_unlocked":
      return {
        icon: <Sparkles className={baseClass} />,
        label: "Naujas įgūdis atrakintas",
        detail: event.skillName || null,
        bg: "bg-cyan-100 dark:bg-cyan-950/40",
        ring: "ring-cyan-200 dark:ring-cyan-900",
        iconColor: "text-cyan-600 dark:text-cyan-400",
      };
    case "personal_best":
      return {
        icon: <Star className={baseClass} />,
        label: "Naujas asmeninis rekordas",
        detail: event.skillName || null,
        bg: "bg-yellow-100 dark:bg-yellow-950/40",
        ring: "ring-yellow-200 dark:ring-yellow-900",
        iconColor: "text-yellow-600 dark:text-yellow-400",
      };
    case "skill_progress":
      return {
        icon: <TrendingUp className={baseClass} />,
        label: "Įgūdžio pažanga",
        detail: event.skillName || null,
        bg: "bg-green-100 dark:bg-green-950/40",
        ring: "ring-green-200 dark:ring-green-900",
        iconColor: "text-green-600 dark:text-green-400",
      };
    case "level_up":
      return {
        icon: <Rocket className={baseClass} />,
        label: event.levelName ? `Naujas lygis: ${event.levelName}` : "Naujas lygis!",
        detail: event.message || null,
        bg: "bg-violet-100 dark:bg-violet-950/40",
        ring: "ring-violet-200 dark:ring-violet-900",
        iconColor: "text-violet-600 dark:text-violet-400",
      };
    case "achievement":
      return {
        icon: event.achievementIcon ? (
          <span className="text-base leading-none">{event.achievementIcon}</span>
        ) : (
          <Trophy className={baseClass} />
        ),
        label: event.achievementName || "Pasiekimas",
        detail: event.message && event.message !== event.achievementName ? event.message : null,
        bg: "bg-rose-100 dark:bg-rose-950/40",
        ring: "ring-rose-200 dark:ring-rose-900",
        iconColor: "text-rose-600 dark:text-rose-400",
      };
    case "points":
      // Streak bonus or generic points
      if (event.message?.includes("serija") || event.message?.includes("dien")) {
        return {
          icon: <Flame className={baseClass} />,
          label: "Aktyvumo serija",
          detail: event.message,
          bg: "bg-orange-100 dark:bg-orange-950/40",
          ring: "ring-orange-200 dark:ring-orange-900",
          iconColor: "text-orange-600 dark:text-orange-400",
        };
      }
      return {
        icon: <Zap className={baseClass} />,
        label: event.message || "Premija",
        bg: "bg-indigo-100 dark:bg-indigo-950/40",
        ring: "ring-indigo-200 dark:ring-indigo-900",
        iconColor: "text-indigo-600 dark:text-indigo-400",
      };
    default:
      return {
        icon: <Star className={baseClass} />,
        label: event.message || event.type || "Premija",
        bg: "bg-muted",
        ring: "ring-border",
        iconColor: "text-muted-foreground",
      };
  }
}

const EVENT_GROUPS: Record<string, "performance" | "skills" | "bonuses"> = {
  base: "performance",
  correct_answers: "performance",
  perfect_session: "performance",
  skill_mastered: "skills",
  skill_unlocked: "skills",
  personal_best: "skills",
  skill_progress: "skills",
  achievement: "bonuses",
  level_up: "bonuses",
  points: "bonuses",
};

function PointsBreakdown({
  pointsEarned,
  events,
  totalProblems,
  correctCount,
}: {
  pointsEarned: number;
  events: GamificationEvent[];
  totalProblems: number;
  correctCount: number;
}) {
  const attributed = events.reduce((sum, e) => sum + (e.points || 0), 0);
  const unattributed = Math.max(0, pointsEarned - attributed);
  const accuracy = totalProblems > 0 ? Math.round((correctCount / totalProblems) * 100) : 0;

  const grouped: Record<"performance" | "skills" | "bonuses", GamificationEvent[]> = {
    performance: [],
    skills: [],
    bonuses: [],
  };
  for (const ev of events) {
    const group = EVENT_GROUPS[ev.type] || "bonuses";
    grouped[group].push(ev);
  }

  const groupMeta: Array<{
    key: "performance" | "skills" | "bonuses";
    title: string;
    subtitle: string;
  }> = [
    { key: "performance", title: "Pasirodymas", subtitle: "Užduočių sprendimas" },
    { key: "skills", title: "Įgūdžių pažanga", subtitle: "Pasiekimai už įgūdžius" },
    { key: "bonuses", title: "Premijos", subtitle: "Pasiekimai, lygiai, serijos" },
  ];

  return (
    <div className="mt-6 pt-6 border-t space-y-5">
      {/* Hero total */}
      <div className="relative overflow-hidden rounded-2xl border border-amber-200/60 dark:border-amber-900/60 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-amber-950/40 dark:via-orange-950/40 dark:to-yellow-950/40 p-5">
        <div className="absolute -right-8 -top-8 opacity-10 dark:opacity-20">
          <Coins className="h-32 w-32 text-amber-600" />
        </div>
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-amber-700/80 dark:text-amber-300/80">
              Iš viso uždirbta
            </p>
            <p className="text-4xl font-extrabold text-amber-700 dark:text-amber-300 tabular-nums leading-tight">
              +{pointsEarned}
              <span className="ml-1.5 text-base font-semibold text-amber-700/70 dark:text-amber-400/70">
                taškų
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <MiniStat label="Atsakymai" value={`${correctCount}/${totalProblems}`} />
            <MiniStat label="Tikslumas" value={`${accuracy}%`} accent={accuracy >= 90} />
            <MiniStat label="Įvykiai" value={`${events.length}`} />
          </div>
        </div>
      </div>

      {/* Grouped breakdown */}
      {events.length > 0 ? (
        <div className="space-y-4">
          {groupMeta.map(({ key, title, subtitle }) => {
            const items = grouped[key];
            if (!items.length) return null;
            const groupTotal = items.reduce((s, e) => s + (e.points || 0), 0);
            return (
              <div key={key} className="rounded-xl border bg-card overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-muted/40 border-b">
                  <div>
                    <p className="text-sm font-semibold">{title}</p>
                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                  </div>
                  {groupTotal > 0 && (
                    <span className="text-sm font-bold text-amber-600 dark:text-amber-400 tabular-nums">
                      +{groupTotal}
                    </span>
                  )}
                </div>
                <ul className="divide-y">
                  {items.map((event, i) => {
                    const v = describeEvent(event);
                    return (
                      <li
                        key={i}
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors"
                      >
                        <span
                          className={cn(
                            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ring-1",
                            v.bg,
                            v.ring,
                            v.iconColor
                          )}
                        >
                          {v.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-tight">{v.label}</p>
                          {v.detail && (
                            <p className="text-xs text-muted-foreground truncate">{v.detail}</p>
                          )}
                        </div>
                        {!!event.points && event.points > 0 ? (
                          <span className="text-sm font-bold tabular-nums text-amber-600 dark:text-amber-400">
                            +{event.points}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">be taškų</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
          {unattributed > 0 && (
            <p className="text-xs text-muted-foreground italic px-1">
              + dar {unattributed} taškų — už seriją, naujus įgūdžius ir kitus pasiekimus, kurių
              detalės neišsaugotos.
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          Šios sesijos taškų detalės neišsaugotos.
        </p>
      )}
    </div>
  );
}

function MiniStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-background/80 backdrop-blur-sm px-3 py-1.5 text-center min-w-[68px]",
        accent && "border-emerald-300 dark:border-emerald-800"
      )}
    >
      <p
        className={cn(
          "text-base font-bold tabular-nums leading-tight",
          accent ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
        )}
      >
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
    </div>
  );
}
